import React, { useState, useEffect } from 'react';
import {
  Search,
  Plus,
  Edit,
  Trash2,
  Building2,
  Filter
} from 'lucide-react';
import companyService from '../services/companyService';
import {
  EmpresaResponseDTO,
  CompanySearchParams,
  CompanyState
} from '../types/company';
import CompanyModal from '../components/companies/CompanyModal';
import alertService from '../services/alertService';

const CompaniesPage: React.FC = () => {
  const [state, setState] = useState<CompanyState>({
    companies: [],
    loading: false,
    error: null,
    totalPages: 0,
    currentPage: 0,
    totalElements: 0
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<EmpresaResponseDTO | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    isMatriz: '',
    ativo: ''
  });

  const pageSize = 10;

  // Carregar empresas
  const loadCompanies = async (page: number = 0, searchParams?: CompanySearchParams) => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const params: CompanySearchParams = {
        page,
        size: pageSize,
        sort: 'nomeFantasia',
        direction: 'ASC',
        ...searchParams
      };

      // Adicionar filtros se existirem
      if (filters.isMatriz || filters.ativo) {
        params.filters = {};
        if (filters.isMatriz) params.filters.isMatriz = filters.isMatriz === 'true';
        if (filters.ativo) params.filters.ativo = filters.ativo === 'true';
      }

      const response = await companyService.getCompanies(params);
      
      setState(prev => ({
        ...prev,
        companies: response.content,
        totalPages: response.totalPages,
        currentPage: response.number,
        totalElements: response.totalElements,
        loading: false
      }));
    } catch (error) {
      console.error('Erro ao carregar empresas:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Erro ao carregar empresas. Tente novamente.'
      }));
      alertService.error('Erro ao carregar empresas');
    }
  };

  // Buscar empresas por termo
  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      loadCompanies(0);
      return;
    }

    setState(prev => ({ ...prev, loading: true, error: null }));
    
    try {
      const companies = await companyService.searchCompanies(searchTerm);
      setState(prev => ({
        ...prev,
        companies,
        totalPages: 1,
        currentPage: 0,
        totalElements: companies.length,
        loading: false
      }));
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Erro ao buscar empresas. Tente novamente.'
      }));
      alertService.error('Erro ao buscar empresas');
    }
  };

  // Excluir empresa
  const handleDelete = async (company: EmpresaResponseDTO) => {
    const result = await alertService.confirm(
      'Inativar empresa',
      `Tem certeza que deseja inativar a empresa "${company.nomeFantasia}"?`,
      'Sim, inativar',
      'Cancelar'
    );
    
    if (!result.isConfirmed) {
      return;
    }

    try {
      await companyService.deleteCompany(company.id);
      alertService.success('Empresa inativada com sucesso!');
      loadCompanies(state.currentPage);
    } catch (error) {
      console.error('Erro ao inativar empresa:', error);
      alertService.error('Erro ao inativar empresa');
    }
  };

  // Abrir modal para edição
  const handleEdit = (company: EmpresaResponseDTO) => {
    setSelectedCompany(company);
    setShowModal(true);
  };

  // Abrir modal para criação
  const handleCreate = () => {
    setSelectedCompany(null);
    setShowModal(true);
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedCompany(null);
  };

  // Callback após salvar
  const handleSave = () => {
    handleCloseModal();
    loadCompanies(state.currentPage);
  };

  // Aplicar filtros
  const handleApplyFilters = () => {
    loadCompanies(0);
    setShowFilters(false);
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({ isMatriz: '', ativo: '' });
    setSearchTerm('');
    loadCompanies(0);
    setShowFilters(false);
  };

  // Mudança de página
  const handlePageChange = (page: number) => {
    loadCompanies(page);
  };

  // Formatação
  const formatCNPJ = (cnpj: string) => {
    return companyService.formatCNPJ(cnpj);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  // Carregar dados iniciais
  useEffect(() => {
    loadCompanies();
  }, []);

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <Building2 className="h-6 w-6 sm:h-8 sm:w-8 text-red-600" />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Empresas</h1>
              <p className="text-sm sm:text-base text-gray-600">Gerencie as empresas do sistema</p>
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors w-full sm:w-auto justify-center"
          >
            <Plus className="h-5 w-5" />
            <span>Nova Empresa</span>
          </button>
        </div>
      </div>

      {/* Barra de busca e filtros */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-6">
        <div className="flex flex-col gap-3 sm:gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="h-4 w-4 sm:h-5 sm:w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar por nome fantasia, razão social ou CNPJ..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full pl-9 sm:pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <button
              onClick={handleSearch}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors text-sm sm:text-base order-1"
            >
              Buscar
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm sm:text-base order-2"
            >
              <Filter className="h-4 w-4 sm:h-5 sm:w-5" />
              <span>Filtros</span>
            </button>
          </div>
        </div>

        {/* Filtros expandidos */}
        {showFilters && (
          <div className="mt-3 sm:mt-4 pt-3 sm:pt-4 border-t border-gray-200">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo
                </label>
                <select
                  value={filters.isMatriz}
                  onChange={(e) => setFilters(prev => ({ ...prev, isMatriz: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm sm:text-base"
                >
                  <option value="">Todos</option>
                  <option value="true">Matriz</option>
                  <option value="false">Filial</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={filters.ativo}
                  onChange={(e) => setFilters(prev => ({ ...prev, ativo: e.target.value }))}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                >
                  <option value="">Todos</option>
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </select>
              </div>
              <div className="sm:col-span-2 flex flex-col sm:flex-row gap-2">
                <button
                  onClick={handleApplyFilters}
                  className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
                >
                  Aplicar Filtros
                </button>
                <button
                  onClick={handleClearFilters}
                  className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-lg transition-colors text-sm sm:text-base"
                >
                  Limpar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mensagem de erro */}
      {state.error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {state.error}
        </div>
      )}

      {/* Tabela - Desktop */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hidden md:block">
        {state.loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <span className="ml-2 text-gray-600">Carregando...</span>
          </div>
        ) : state.companies.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma empresa encontrada</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Empresa
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      CNPJ
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Código
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tipo
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Criado em
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Ações
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {state.companies.map((company) => (
                    <tr key={company.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {company.nomeFantasia}
                          </div>
                          <div className="text-sm text-gray-500">
                            {company.razaoSocial}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCNPJ(company.cnpj)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {company.codigoIdentificador}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          company.isMatriz 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {company.isMatriz ? 'Matriz' : 'Filial'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          company.ativo 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {company.ativo ? 'Ativo' : 'Inativo'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(company.criadoEm)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(company)}
                            className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                            title="Editar"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(company)}
                            className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                            title="Inativar"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginação - Desktop */}
            {state.totalPages > 1 && (
              <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6 hidden md:block">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Mostrando{' '}
                      <span className="font-medium">{state.currentPage * pageSize + 1}</span>
                      {' '}até{' '}
                      <span className="font-medium">
                        {Math.min((state.currentPage + 1) * pageSize, state.totalElements)}
                      </span>
                      {' '}de{' '}
                      <span className="font-medium">{state.totalElements}</span>
                      {' '}resultados
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                      <button
                        onClick={() => handlePageChange(state.currentPage - 1)}
                        disabled={state.currentPage === 0}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Anterior
                      </button>
                      {Array.from({ length: Math.min(5, state.totalPages) }, (_, i) => {
                        const page = state.currentPage < 3 ? i : state.currentPage - 2 + i;
                        if (page >= state.totalPages) return null;
                        return (
                          <button
                            key={page}
                            onClick={() => handlePageChange(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              page === state.currentPage
                                ? 'z-10 bg-red-50 border-red-500 text-red-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page + 1}
                          </button>
                        );
                      })}
                      <button
                        onClick={() => handlePageChange(state.currentPage + 1)}
                        disabled={state.currentPage >= state.totalPages - 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Próximo
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Cards - Mobile */}
      <div className="md:hidden">
        {state.loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
            <span className="ml-2 text-gray-600">Carregando...</span>
          </div>
        ) : state.companies.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">Nenhuma empresa encontrada</p>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {state.companies.map((company) => (
                <div key={company.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 mb-1">
                        {company.nomeFantasia}
                      </h3>
                      <p className="text-xs text-gray-500">
                        {company.razaoSocial}
                      </p>
                    </div>
                    <div className="flex space-x-2 ml-2">
                      <button
                        onClick={() => handleEdit(company)}
                        className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                        title="Editar"
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(company)}
                        className="text-red-600 hover:text-red-900 p-1 rounded transition-colors"
                        title="Inativar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">CNPJ:</span>
                      <span className="text-xs text-gray-900">{formatCNPJ(company.cnpj)}</span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Código:</span>
                      <span className="text-xs text-gray-900">{company.codigoIdentificador}</span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Tipo:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        company.isMatriz 
                          ? 'bg-purple-100 text-purple-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.isMatriz ? 'Matriz' : 'Filial'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-500">Status:</span>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        company.ativo 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {company.ativo ? 'Ativo' : 'Inativo'}
                      </span>
                    </div>
                    
                    <div className="flex justify-between">
                      <span className="text-xs text-gray-500">Criado em:</span>
                      <span className="text-xs text-gray-900">{formatDate(company.criadoEm)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Paginação Mobile */}
            {state.totalPages > 1 && (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mt-4">
                <div className="flex items-center justify-between">
                  <button
                    onClick={() => handlePageChange(state.currentPage - 1)}
                    disabled={state.currentPage === 0}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <span className="text-sm text-gray-700">
                    Página {state.currentPage + 1} de {state.totalPages}
                  </span>
                  <button
                    onClick={() => handlePageChange(state.currentPage + 1)}
                    disabled={state.currentPage >= state.totalPages - 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Próximo
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <CompanyModal
          company={selectedCompany}
          onClose={handleCloseModal}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default CompaniesPage;